<template>
  <div>
    <b-button @click="openFingerPrint()">
      <i class="mdi mdi-fingerprint"></i>
    </b-button>
    <b-button @click="startVerify()" variant="primary">Verify OTP</b-button>

    <!-- Consent Modal -->
    <b-modal
      v-model="consentDialog"
      hide-footer
      centered
      title="Consent for Aadhaar Verification"
      size="lg"
      no-close-on-backdrop
    >
      <p class="text-muted small mb-2">
        Please read and accept the following consent as per ABDM / National Health Authority guidelines.
      </p>
      <div class="border rounded p-3 bg-light mb-3" style="max-height: 350px; overflow-y: auto; font-size: 13px;">
        <p class="mb-1"><strong>I hereby declare that:</strong></p>
        <b-form-checkbox v-model="consent.aadhaar_sharing" class="mb-2">
          I am voluntarily sharing my Aadhaar Number / Virtual ID issued by the Unique Identification
          Authority of India ("UIDAI"), and my demographic information for the purpose of verifying my
          identity. I authorize NHA to use my Aadhaar number / Virtual ID for performing Aadhaar based
          authentication with UIDAI as per the provisions of the Aadhaar (Targeted Delivery of Financial
          and other Subsidies, Benefits and Services) Act, 2016 for the aforesaid purpose.
        </b-form-checkbox>
        <b-form-checkbox v-model="consent.health_records" class="mb-2">
          I authorize the sharing of all my health records with healthcare provider(s) for the
          purpose of providing healthcare services to me during this encounter.
        </b-form-checkbox>
        <hr class="my-2" />
        <b-form-checkbox v-model="consent.worker_confirm" class="mb-2">
          I, <strong>{{ workerName || '(Healthcare Worker)' }}</strong>, confirm that I have duly
          informed and explained the beneficiary of the contents of consent for aforementioned purposes.
        </b-form-checkbox>
        <b-form-checkbox v-model="consent.beneficiary_confirm" class="mb-2">
          I, <strong>{{ beneficiaryName || '(Beneficiary)' }}</strong>, have been explained about
          the consent as stated above and hereby provide my consent for the aforementioned purposes.
        </b-form-checkbox>
      </div>
      <b-alert v-if="consentError" show variant="danger" class="py-1 px-2 mb-2">
        <small>{{ consentError }}</small>
      </b-alert>
      <div class="d-flex justify-content-end">
        <b-button variant="text" class="text-danger mr-2" @click="consentDialog = false">
          Cancel
        </b-button>
        <b-button variant="primary" @click="validateConsent()">
          I Agree &amp; Proceed
        </b-button>
      </div>
    </b-modal>

    <!-- OTP Entry Modal -->
    <b-modal
      v-model="otpDialog"
      hide-footer
      centered
      title="Verify Aadhaar OTP"
      size="sm"
    >
      <p class="text-muted small mb-2">
        <template v-if="maskedMobile">
          OTP sent to Aadhaar-linked mobile number ending with <strong>{{ maskedMobile }}</strong>.
        </template>
        <template v-else>
          Enter the 6-digit OTP sent to your Aadhaar-linked mobile number.
        </template>
      </p>
      <form-wrapper @submit="verifyOtp()">
        <input-field
          label="Enter 6-digit OTP"
          type="number"
          rules="required"
          v-model="otp"
        ></input-field>
        <input-field
          label="Communication Mobile Number"
          type="number"
          rules="required"
          v-model="mobile"
          placeholder="Enter 10-digit mobile number"
        ></input-field>
        <div class="mb-3">
          <b-button
            variant="link"
            size="sm"
            :disabled="resendTimer > 0 || resendCount >= 2"
            @click="resendOtp()"
            class="p-0"
          >
            <template v-if="resendTimer > 0">Resend OTP in {{ resendTimer }}s</template>
            <template v-else-if="resendCount >= 2">Maximum resend attempts reached</template>
            <template v-else>Resend OTP</template>
          </b-button>
        </div>
        <div class="d-flex">
          <div class="flex-grow-1"></div>
          <b-button variant="text" class="text-danger mx-3" @click="otpDialog = false">
            Cancel
          </b-button>
          <b-button variant="text" class="text-success" type="submit" :disabled="isLoading">
            <b-spinner small v-if="isLoading" class="mr-1"></b-spinner>
            Verify
          </b-button>
        </div>
      </form-wrapper>
    </b-modal>
  </div>
</template>

<script>
import abdmService from "../../../assets/services/abdm.service";
import recipientService from "../../../assets/services/recipient.service";
export default {
  props: {
    number: {},
  },
  data() {
    return {
      consentDialog: false,
      otpDialog: false,
      otp: "",
      mobile: "",
      txnId: "",
      maskedMobile: "",
      isLoading: false,
      resendCount: 0,
      resendTimer: 0,
      resendInterval: null,
      consent: {
        aadhaar_sharing: true,
        health_records: true,
        worker_confirm: false,
        beneficiary_confirm: false,
      },
      consentError: "",
    };
  },
  computed: {
    workerName() {
      try {
        return this.$store.getters.userName || this.$store.state.userName || "";
      } catch (e) {
        return "";
      }
    },
    beneficiaryName() {
      return "";
    },
  },
  watch: {
    otpDialog() {
      this.otp = "";
    },
  },
  methods: {
    startVerify() {
      const vm = this;
      if (!vm.number || vm.number.toString().length != 12) {
        return vm.$alert.show("This Aadhaar ID must be 12 digit number");
      }
      vm.consentError = "";
      vm.consent = {
        aadhaar_sharing: true,
        health_records: true,
        worker_confirm: false,
        beneficiary_confirm: false,
      };
      vm.consentDialog = true;
    },
    validateConsent() {
      this.consentError = "";
      if (!this.consent.aadhaar_sharing) {
        this.consentError = "You must consent to Aadhaar sharing to proceed.";
        return;
      }
      if (!this.consent.worker_confirm) {
        this.consentError = "Healthcare worker must confirm that consent has been explained.";
        return;
      }
      if (!this.consent.beneficiary_confirm) {
        this.consentError = "Beneficiary must confirm consent.";
        return;
      }
      this.consentDialog = false;
      this.generateOtp();
    },
    async generateOtp() {
      const vm = this;
      try {
        vm.$loader.show();
        let { data } = await abdmService.enrollmentRequestOtp({
          aadhaarNumber: vm.number,
        });
        vm.$loader.hide();
        if (!data.status) {
          vm.$alert.show(data.msg || "Failed to send OTP.");
          return;
        }
        vm.txnId = data.txnId;
        vm.maskedMobile = data.mobileNumber || "";
        vm.otp = "";
        vm.resendCount = 0;
        vm.resendTimer = 0;
        vm.otpDialog = true;
        vm.startResendTimer();
      } catch (error) {
        vm.$loader.hide();
        vm.$alert.show(error.message || "Failed to send OTP. Please try again.");
      }
    },
    async resendOtp() {
      if (this.resendCount >= 2) return;
      this.resendCount++;
      const vm = this;
      try {
        vm.isLoading = true;
        let { data } = await abdmService.enrollmentRequestOtp({
          aadhaarNumber: vm.number,
        });
        vm.isLoading = false;
        if (!data.status) {
          vm.$alert.show(data.msg || "Failed to resend OTP.");
          return;
        }
        vm.txnId = data.txnId;
        vm.startResendTimer();
      } catch (error) {
        vm.isLoading = false;
        vm.$alert.show(error.message || "Failed to resend OTP.");
      }
    },
    startResendTimer() {
      this.resendTimer = 60;
      clearInterval(this.resendInterval);
      this.resendInterval = setInterval(() => {
        this.resendTimer--;
        if (this.resendTimer <= 0) clearInterval(this.resendInterval);
      }, 1000);
    },
    async verifyOtp() {
      const vm = this;
      try {
        vm.isLoading = true;
        let { data } = await abdmService.enrollByAadhaar({
          transactionId: vm.txnId,
          otp: vm.otp,
          mobile: vm.mobile,
        });
        vm.isLoading = false;
        if (!data.status) {
          vm.$alert.show(data.msg || "OTP verification failed.");
          return;
        }
        vm.otpDialog = false;
        vm.$emit("onVerified");
      } catch (error) {
        vm.isLoading = false;
        vm.$alert.show(error.message || "Verification failed. Please try again.");
      }
    },
    openFingerPrint() {
      const vm = this;
      if (!vm.number || vm.number.toString().length != 12) {
        return vm.$alert.show("This Aadhaar ID must be 12 digit number");
      }
      var urlStr = "http://localhost:11100/rd/capture";
      var getJSONCapture = function (url, callback) {
        var xhr = new XMLHttpRequest();
        xhr.open("CAPTURE", url, true);
        xhr.responseType = "text";
        var InputXml =
          '<PidOptions ver="1.0"><Opts fCount="1" fType="2" iCount="0" pCount="0" format="0" pidVer="2.0" timeout="20000" otp="" wadh="E0jzJ/P8UopUHAieZn8CKqS4WPMi5ZSYXgfnlfkWjrc=" env="P" posh="RIGHT_INDEX" /></PidOptions>';
        xhr.onload = function () {
          var status = xhr.status;
          if (status == 200) {
            callback(null, xhr.response);
          } else {
            callback(status);
          }
        };
        xhr.send(InputXml);
      };
      vm.$loader.show();
      getJSONCapture(urlStr, function (err, data) {
        vm.$loader.hide();
        if (err != null) {
          vm.$alert.show("Fingerprint device error. Please check the RD service.");
          return;
        }
        var convert = require("xml-js");
        var json = JSON.parse(convert.xml2json(data, { compact: true }));
        let message = json?.PidData?.Resp?._attributes?.errInfo;
        if (message != "Success.") {
          vm.$alert.show(message);
          return;
        }
        vm.verifyFingerPrint(data);
      });
    },
    async verifyFingerPrint(text) {
      const vm = this;
      var body = new FormData();
      var strblob = new Blob([text], { type: "text/plain" });
      body.append("file", strblob, "file.txt");
      body.append("aadhar_number", vm.number);
      try {
        vm.$loader.show();
        let { list, status, msg } = (
          await recipientService.verifyFingerPrint(body)
        ).data;
        vm.$loader.hide();
        if (!status) {
          vm.$alert.show(msg);
          return;
        }
        let { ret } = list;
        if (ret == "y") {
          vm.$emit("onVerified");
        } else {
          vm.$alert.show("Fingerprint verification failed. Please try again.");
        }
      } catch (error) {
        vm.$loader.hide();
        vm.$alert.show(error.message);
      }
    },
  },
  beforeDestroy() {
    clearInterval(this.resendInterval);
  },
};
</script>

<style></style>
